<template>
  <v-chip
    v-if="isValidUser"
    :variant="chipVariant"
    pill
    size="small"
    :color="getChipColor()"
  >
    <template #prepend>
      <v-avatar v-if="userAvatarUrl" start>
        <img :src="user.icon" :alt="userName" />
      </v-avatar>
      <v-avatar
        v-else
        size="28"
        class="chip-avatar text-white"
        color="blue"
        start
      >
        {{ userInitial }}
      </v-avatar>
    </template>
    {{ userName }}
    <template #append v-if="isDeleted">
      <v-icon
        size="x-small"
        class="ms-1"
        icon="mdi-account-off-outline"
        title="Verwijderde gebruiker"
      ></v-icon>
    </template>
  </v-chip>
  <v-chip v-else :variant="chipVariant" pill :color="getChipColor()">
    Onbekend
    <template #append>
      <v-avatar size="28" class="chip-avatar text-white" color="grey">
        ?
      </v-avatar>
    </template>
  </v-chip>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  user: {
    type: Object,
    default: () => null,
  },
  small: Boolean,
  // When true, render with a high-contrast style suitable on light backgrounds
  onLight: {
    type: Boolean,
    default: false,
  },
});

// True when the resolved author has been (soft-)deleted.
const isDeleted = computed(() => !!(props.user && props.user.deleted));

// Check if we have a valid user object with required properties
const isValidUser = computed(() => {
  return (
    props.user &&
    (props.user.short ||
      props.user.first_name ||
      props.user.name ||
      props.user.email)
  );
});

// Get the user's avatar URL
const userAvatarUrl = computed(() => {
  // Accept avatar_url or profile_image or similar
  return props.user && props.user.icon;
});

const chipVariant = computed(() => (props.onLight ? "tonal" : "elevated"));

// Get appropriate chip colors for better contrast based on background
const getChipColor = () => {
  // On light backgrounds, use theme color for readable tonal chip
  if (props.onLight) return "primary";
  // Default translucent overlay for darker surfaces
  return "rgba(255, 255, 255, 0.2)";
};

// Get the user's initial from the best available source
const userInitial = computed(() => {
  if (!isValidUser.value) return "?";

  if (props.user.short) {
    return props.user.short.toUpperCase();
  } else if (props.user.first_name) {
    return props.user.first_name.charAt(0).toUpperCase();
  } else if (props.user.name) {
    return props.user.name.charAt(0).toUpperCase();
  } else if (props.user.email) {
    return props.user.email.charAt(0).toUpperCase();
  }

  return "?";
});

// Get the user's display name from the best available source
const userName = computed(() => {
  if (!isValidUser.value) return "Onbekend";

  if (props.user.name) {
    return props.user.name;
  } else if (props.user.first_name) {
    const fullName =
      props.user.first_name +
      (props.user.last_name ? ` ${props.user.last_name}` : "");
    return fullName;
  } else if (props.user.email) {
    return props.user.email.split("@")[0];
  }

  return "Gebruiker";
});
</script>
